import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Row, Col, DatePicker, Typography, Select, Button, Popover } from 'antd';
import { PageHeader } from '../../components/layout/PageHeader';
import { ClientScoring } from '../../types/result';
import { FlowStats } from '../../types/flow';
import { addDatasetToScoringAlgorithm, getScoringOverview } from '../../lib/api/aws-api';
import { getFlowStats } from '../../lib/api/flow-api';
import { AlgorithmCard } from './AlgorithmCard';
import { StatisticBox } from './StatisticBox';
import moment from 'moment';
import { Spinner } from '../../components/loading/Spinner';
import { ErrorText } from '../../components/text/ErrorText';
import useGlobal from '../../store/Store';
import { algorithmTypes } from '../../config/AlgorithmTypes';
import { CONFIG } from '../../config/Config';
import { AlgorithmType } from '../../types';
import { PlusCircleOutlined } from '@ant-design/icons';
import { useAllDatasets } from '../../lib/hooks';

const { MonthPicker } = DatePicker;
const { Text } = Typography;

const ScoreOverview = () => {
  const { datasets } = useAllDatasets();

  const [stats, setStats] = useState<FlowStats | null>(null);
  const [statsLoading, setStatsLoading] = useState<boolean>(false);
  const [selectedMonth, setSelectedMonth] = useState<any>(moment());
  const [scoringIdLookup, setScoringIdLookup] = useState<{ algorithmDataset: string; id: string }[]>([]);
  const [flattenedClientAlgorithms, setFlattenedClientAlgorithms] = useState<ClientScoring[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [globalState] = useGlobal();

  const loadAlgorithms = async () => {
    try {
      setLoading(true);
      const scoringsPerClient = await getScoringOverview();
      const reducedClients = scoringsPerClient.map((val, i, arr) => {
        return {
          ...val,
          datasets: arr.filter(el => el.algorithm_id === val.algorithm_id).map(el => el.dataset_id),
        };
      });
      const array: ClientScoring[] = [];
      const lookup: { algorithmDataset: string; id: string }[] = [];
      reducedClients.forEach(val => {
        lookup.push({ algorithmDataset: val.algorithm_id + '_' + val.dataset_id, id: val.id.toString() });
        const alreadyExists = array.find(el => el.algorithm_id === val.algorithm_id) ? true : false;
        if (!alreadyExists) {
          array.push(val);
        }
      });
      setScoringIdLookup(lookup);
      setFlattenedClientAlgorithms(array);
      setError(false);
    } catch (e) {
      setError(true);
      setScoringIdLookup([]);
      setFlattenedClientAlgorithms([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!loading && !scoringIdLookup) {
      loadAlgorithms();
    }
  }, [loading, scoringIdLookup]);

  useEffect(() => {
    loadAlgorithms();
  }, [globalState.refreshOverview]);

  const fetch = useCallback(async () => {
    if (selectedMonth) {
      const datestring = selectedMonth.format('MMYYYY');
      setStatsLoading(true);
      const s = await getFlowStats(datestring);
      console.log(s);
      setStats(s);
      setStatsLoading(false);
    }
  }, [selectedMonth]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  // Adding new algorithm card

  const algorithmSelection = useMemo(() => {
    return (CONFIG.algorithms
      .map(algorithm => {
        return algorithmTypes.find(alg => alg.id === algorithm.id);
      })
      .filter(algorithm => !!algorithm) as AlgorithmType[]).filter(
      algorithm => !flattenedClientAlgorithms.find(alg => +alg.algorithm_id === algorithm.id)
    );
  }, [flattenedClientAlgorithms]);

  const [selectedAlgorithm, setSelectedAlgorithm] = useState<string | null>(null);

  const addNewScoreCard = useCallback(async () => {
    // since a scoreCard needs at least one dataset, so we just add the first dataset
    const dataset = datasets.find(dataset => dataset);
    if (selectedAlgorithm && dataset) await addDatasetToScoringAlgorithm(dataset.id, selectedAlgorithm);
    setSelectedAlgorithm(null);
    loadAlgorithms();
  }, [datasets, selectedAlgorithm]);

  return (
    <>
      <PageHeader
        container="overview"
        header="Algorithm Overview"
        description="This is an overview of all algorithms used in your document processing flows."
      />
      <Row className="flex-col mb-6">
        <Row style={{ alignItems: 'center' }}>
          <Text style={{ marginRight: 12 }}>Select a month:</Text>
          <MonthPicker
            format="MMM YYYY"
            value={selectedMonth}
            onChange={date => {
              setSelectedMonth(date);
            }}
          />
        </Row>
        <Row gutter={16} className="mt-3">
          <Col span={6}>
            <StatisticBox label="Total Documents" stat={stats?.total_docs} loading={statsLoading} />
          </Col>
          <Col span={6}>
            <StatisticBox label="Total Pages" stat={stats?.total_pages} loading={statsLoading} />
          </Col>
          <Col span={6}>
            <StatisticBox
              label="Average Runtime"
              stat={
                stats?.avg_run_time
                  ? stats.avg_run_time.seconds + '.' + stats.avg_run_time.milliseconds.toString().substring(0, 1) + 's '
                  : 'No info'
              }
              loading={statsLoading}
            />
          </Col>
        </Row>

        {/* Add again if we need extra information we want to show */}
        {/* <Row>
          <Col span={12}>
            <Button
              onClick={() => setShowExtraInfo(prev => !prev)}
              type="text"
              style={{
                fontSize: '16px',
              }}>
              {showExtraInfo ? <Icon type="caret-down" /> : <Icon type="caret-right" />} Extra information
            </Button>
            {showExtraInfo && (
              <Descriptions title="" className="mt-4 ml-6">
                <Descriptions.Item label="Average Runtime">
                  {stats?.avg_run_time
                    ? stats.avg_run_time.seconds +
                      '.' +
                      stats.avg_run_time.milliseconds.toString().substring(0, 1) +
                      's '
                    : 'No info'}
                </Descriptions.Item>
              </Descriptions>
            )}
          </Col>
        </Row> */}
      </Row>

      <Row justify="end" align="middle" className="mb-4">
        <Popover
          onVisibleChange={() => {
            setSelectedAlgorithm(null);
          }}
          content={
            <Row align="middle" className="gap-2">
              <Select
                value={selectedAlgorithm || undefined}
                onChange={setSelectedAlgorithm}
                style={{ width: 240 }}
                placeholder="Choose an algorithm...">
                {algorithmSelection.map(algorithm => (
                  <Select.Option key={algorithm.id} value={algorithm.id}>
                    {algorithm.value}
                  </Select.Option>
                ))}
              </Select>
              <Button
                type="primary"
                disabled={!selectedAlgorithm}
                onClick={() => {
                  addNewScoreCard();
                }}>
                Add
              </Button>
            </Row>
          }
          trigger={['click']}>
          <Button icon={<PlusCircleOutlined />}>Add a scorecard</Button>
        </Popover>
      </Row>
      <Row justify="start" gutter={[20, 20]}>
        {flattenedClientAlgorithms.length && !loading
          ? flattenedClientAlgorithms.map((clientAlgorithm, index) => (
              <AlgorithmCard key={index} clientAlgorithm={clientAlgorithm} scoringIdLookup={scoringIdLookup} />
            ))
          : null}
        {!flattenedClientAlgorithms.length && error && !loading ? (
          <ErrorText>Something went wrong. No client algorithms found</ErrorText>
        ) : null}
        {loading ? <Spinner /> : null}
      </Row>
    </>
  );
};

export default ScoreOverview;
